import * as Dfns from 'date-fns/fp';
import {
  RiskSignalTag,
  RiskSignalTags,
  SIGNAL_KIND_TAGS,
  SignalKind,
  SignalKindTag
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  ClientHealthBand,
  ClientHealthBandCounts,
  ClientHealthTagCounts,
  ClientHealthWeek,
  GetClientHealthCountsResponse
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { URL_PARAMS } from '@src/Pages/ActivityFeed/activity-feed-constants';
import { getImportanceDisplay } from '@src/Pages/ActivityFeed/activity-feed-utils';
import {
  DEFAULT_FILTER_STATE,
  SIGNAL_OPTIONS
} from '@src/Pages/ActivityFeed/Filters/activity-feed-filters-constants';
import { filterStateToParams } from '@src/Pages/ActivityFeed/Filters/activity-feed-filters-utils';
import {
  CLIENT_HEALTH_LOAD_MORE_THRESHOLD,
  ClientHealthSignalsFilter,
  RISK_FILTER_ALL,
  RiskFilterType,
  SignalSelection,
  WeeklySignalRow
} from './client-health-constants';

export const riskTagsFor = (filter: RiskFilterType): readonly RiskSignalTag[] =>
  filter === RISK_FILTER_ALL ? RiskSignalTags : [filter];

const EMPTY_BAND_COUNTS: ClientHealthBandCounts = { high: 0, medium: 0, low: 0 };

const addBandCounts = (
  a: ClientHealthBandCounts,
  b: ClientHealthBandCounts
): ClientHealthBandCounts => ({
  high: a.high + b.high,
  medium: a.medium + b.medium,
  low: a.low + b.low
});

const tagsFor = (signal: SignalKind, filter: RiskFilterType): readonly SignalKindTag[] =>
  signal === SignalKind.RISK ? riskTagsFor(filter) : SIGNAL_KIND_TAGS[signal];

export const countsFor = (
  counts: ClientHealthTagCounts,
  signal: SignalKind,
  filter: RiskFilterType
): ClientHealthBandCounts =>
  tagsFor(signal, filter).reduce((sum, tag) => addBandCounts(sum, counts[tag]), EMPTY_BAND_COUNTS);

export const hiddenCountFor = (
  hidden: GetClientHealthCountsResponse['hidden'],
  signal: SignalKind,
  filter: RiskFilterType
): number => tagsFor(signal, filter).reduce((sum, tag) => sum + hidden[tag], 0);

// Week starts arrive as UTC dates; parsing at local midnight keeps the label on the same day.
const weekLabel = (weekStart: string): string => Dfns.format('MMM d', Dfns.parseISO(weekStart));

const selectBandCounts = (
  counts: ClientHealthBandCounts,
  signal: SignalKind,
  selection: SignalSelection | null
): ClientHealthBandCounts => {
  if (!selection) return counts;
  if (selection.signal !== signal) return EMPTY_BAND_COUNTS;
  return { ...EMPTY_BAND_COUNTS, [selection.band]: counts[selection.band] };
};

export const toWeeklySignalRows = (
  weeks: ClientHealthWeek[],
  filter: RiskFilterType,
  selection: SignalSelection | null
): WeeklySignalRow[] =>
  weeks.map(week => {
    const positive = selectBandCounts(
      countsFor(week.counts, SignalKind.POSITIVE, filter),
      SignalKind.POSITIVE,
      selection
    );
    const risk = selectBandCounts(
      countsFor(week.counts, SignalKind.RISK, filter),
      SignalKind.RISK,
      selection
    );
    return {
      label: weekLabel(week.weekStart),
      positiveHigh: positive.high,
      positiveMedium: positive.medium,
      positiveLow: positive.low,
      riskHigh: risk.high,
      riskMedium: risk.medium,
      riskLow: risk.low,
      total: positive.high + positive.medium + positive.low + risk.high + risk.medium + risk.low
    };
  });

export const isNearListEnd = ({
  scrollHeight,
  scrollTop,
  clientHeight
}: Pick<HTMLElement, 'scrollHeight' | 'scrollTop' | 'clientHeight'>): boolean =>
  scrollHeight - scrollTop - clientHeight < CLIENT_HEALTH_LOAD_MORE_THRESHOLD;

export const isSameSelection = (a: SignalSelection | null, b: SignalSelection): boolean =>
  a?.signal === b.signal && a.band === b.band;

export const isHighlighted = (
  selection: SignalSelection | null,
  signal: SignalKind,
  band: ClientHealthBand
): boolean => !selection || (selection.signal === signal && selection.band === band);

export const signalsFilterFor = (
  signal: SignalKind,
  selection: SignalSelection | null,
  riskFilter: RiskFilterType
): ClientHealthSignalsFilter => ({
  ...(selection?.signal === signal && { importance: selection.band }),
  ...(signal === SignalKind.RISK && riskFilter !== RISK_FILTER_ALL && { signalTag: riskFilter })
});

export const selectionLabel = ({ signal, band }: SignalSelection): string => {
  const signalLabel = SIGNAL_OPTIONS.find(option => option.value === signal)?.label ?? signal;
  return `Showing ${getImportanceDisplay(band).label} ${signalLabel} Signals only`;
};

export const activityFeedEventPath = (cid: string, eventId: string): string =>
  `/${cid}/activity-feed?${new URLSearchParams({ [URL_PARAMS.SELECTED]: eventId })}`;

export const activityFeedSignalPath = (cid: string, signal: SignalKind): string =>
  `/${cid}/activity-feed?${filterStateToParams({ ...DEFAULT_FILTER_STATE, signals: [signal] })}`;

// parseISO reads a day-precision 'YYYY-MM-DD' as a local day; `new Date` would read UTC midnight.
export const formatSignalDate = (date: string): string => Dfns.format('M/d', Dfns.parseISO(date));
